import React, { useState } from 'react';
import { Alert, Form, Select, Spin, Button } from 'antd';
import { IContact } from 'models';
import { useContactsContext, ContactsContextProvider } from './contacts-context';
import { NewContactModal } from './contact-new-modal';

const Option = Select.Option;

export interface SelectedOption {
  id: string;
  fullName: string;
}

interface ContactsDropdownProps {
  name: string;
  value?: SelectedOption;
  onChange?: (item: IContact) => void;
  hideAll?: boolean;
  showCreateNew?: boolean;
  disabled?: boolean;
  required?: boolean;
}

const ALL_OPTION = { id: '-1', fullName: 'All contacts' };

export function ContactsDropdown(props: ContactsDropdownProps) {
  return (
    <ContactsContextProvider>
      <Dropdown {...props} />
    </ContactsContextProvider>
  );
}

function Dropdown(props: ContactsDropdownProps) {
  const [value, setValue] = useState<string | undefined>(props.value?.id);

  const formInstance = Form.useFormInstance();
  const contactsContext = useContactsContext();

  const { loading, error, data } = contactsContext.contactActions.list.hook; // TODO: Add params

  const onSave = (obj: IContact) => {
    const value = obj.id;

    const values = formInstance.getFieldsValue();
    formInstance.setFieldsValue({ ...values, [props.name]: value });

    setValue(value);

    if (props.onChange) {
      props.onChange(obj);
    }
  }

  const handleChange = (value: string) => {
    setValue(value);
    if (props.onChange) {
      const obj = data?.find((i) => i.id === value);
      props.onChange(obj!);
    }
  };

  const renderOptions = () => {
    if (loading) {
      if (props.value?.id) {
        return [
          <Option key={props.value.id} value={props.value.id}>
            {props.value.fullName}
          </Option>,
        ];
      } else if (props.hideAll) {
        return [];
      }
    }

    const options = [];

    if (!props.hideAll) {
      options.push(ALL_OPTION);
    }

    if (data) {
      data.forEach((i) => options.push(i));
    }

    return options.map((i) => (
      <Option key={i.id} value={i.id}>
        {i.fullName}
      </Option>
    ));
  };

  const renderEmptyOrNotFoundContent = () => {
    if (loading) {
      return <Spin size="small" />;
    }
    if (error) {
      return <Alert showIcon={true} style={{ width: '100%' }} message={error} type="error" />;
    }
    return null;
  };

  const getExtras = () => {
    const extras = [];
    if (props.showCreateNew && contactsContext.contactActions.permissions.hasPermissionCreate) {
      extras.push(
        <Button key="0" type="link" onClick={contactsContext.contactActions.modal.newContact}>New contact</Button>
      );
    }
    return extras.length > 0 ? extras : null;
  }

  return (
    <>
      <Form.Item name={props.name} label="Contact" rules={[{ required: props.required, message: `Contact is required` }]} extra={getExtras()}>
        <Select style={{ width: '100%' }} value={value} disabled={props.disabled} placeholder="Select contact" onChange={handleChange} loading={loading} notFoundContent={renderEmptyOrNotFoundContent()}>
          {renderOptions()}
        </Select>
      </Form.Item>
      <NewContactModal onSave={onSave} />
    </>
  )
}
